import React from "react";
import { motion } from "framer-motion";
import { Plane, CalendarPlus } from "lucide-react";
import { Chapter } from "./Reveal";
import { WorldMap, HeartIcon } from "./Deco";
import { WEDDING } from "../content";
import { downloadWeddingICS } from "../calendar";

export const BoardingPass = ({ t, lang = "tr" }) => {
  const f = WEDDING.flight;
  return (
    <section id="boarding" className="relative leather py-24 sm:py-32 px-4">
      <div className="max-w-5xl mx-auto text-center">
        <Chapter dark>{t.chapter}</Chapter>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 90, rotateX: 12 }}
        whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        style={{ perspective: 1000 }}
        className="relative max-w-5xl mx-auto"
      >
        <div className="relative flex rounded-2xl overflow-hidden shadow-[0_40px_90px_rgba(0,0,0,0.55)]">
          {/* left green stub */}
          <div className="relative w-12 sm:w-16 leather border-r-2 border-dashed border-[var(--gold)]/40 flex flex-col items-center justify-between py-6">
            <Plane className="text-[var(--gold)] rotate-45" size={20} />
            <span className="gold-text font-serif tracking-[0.3em] text-xs sm:text-base uppercase"
              style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
              {t.airline}
            </span>
            <HeartIcon size={14} className="text-[var(--gold)]" />
          </div>

          {/* main body */}
          <div className="relative flex-1 paper p-6 sm:p-10">
            <div className="absolute inset-0 opacity-[0.13] pointer-events-none">
              <WorldMap className="w-full h-full" color="#8a6d2f" />
            </div>
            <div className="relative">
              <p className="font-mono text-[10px] tracking-[0.25em] text-[var(--olive)]/60 uppercase">
                PASSENGERS / YOLCULAR
              </p>
              <h3 className="font-serif text-2xl sm:text-4xl text-[var(--forest)] leading-tight mt-1">
                {WEDDING.bride}
                <span className="block text-xl sm:text-2xl text-[var(--gold-dark)]">&amp;</span>
                {WEDDING.groom}
              </h3>

              {/* route */}
              <div className="mt-8 grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-6">
                <div>
                  <p className="font-mono text-[9px] tracking-[0.2em] text-[var(--olive)]/60">{t.from}</p>
                  <p className="font-serif font-bold text-4xl sm:text-6xl text-[var(--forest)] leading-none">{f.from}</p>
                  <p className="font-sans text-xs text-[var(--olive)]/70 mt-1">{f.fromCity}</p>
                </div>
                <FlightPath />
                <div className="text-right sm:text-left">
                  <p className="font-mono text-[9px] tracking-[0.2em] text-[var(--olive)]/60">{t.to}</p>
                  <p className="font-serif font-bold text-4xl sm:text-6xl text-[var(--forest)] leading-none">{f.to}</p>
                  <p className="font-sans text-xs text-[var(--olive)]/70 mt-1">{f.toCity}</p>
                </div>
              </div>

              {/* details */}
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-y-4 gap-x-3 border-t border-[var(--gold-dark)]/30 pt-5">
                <Cell label={t.flight} value={f.no} />
                <Cell label={t.date} value={f.date} />
                <Cell label={t.boardingTime} value={f.boarding} />
                <Cell label={t.gate} value={f.gate} />
                <Cell label={t.seat} value={f.seat} />
              </div>

              <p className="mt-7 font-serif tracking-[0.15em] text-sm sm:text-base text-[var(--forest)] flex items-center gap-2">
                {t.footer} <HeartIcon size={13} className="text-[var(--gold-dark)]" />
              </p>

              <button
                data-testid="add-calendar-btn"
                onClick={() => downloadWeddingICS(lang)}
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--forest)] text-[var(--cream)] text-[11px] sm:text-xs tracking-[0.18em] uppercase font-sans transition-all duration-300 hover:bg-[var(--olive)] hover:gap-3"
              >
                <CalendarPlus size={14} /> {t.addToCalendar}
              </button>
            </div>
          </div>

          {/* right stub with barcode (hidden on mobile to avoid overflow) */}
          <div className="relative hidden sm:flex w-52 paper border-l-2 border-dashed border-[var(--olive)]/40 p-6 flex-col">
            <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full leather" />
            <div className="leather absolute top-0 left-0 right-0 h-9 flex items-center justify-center gap-2">
              <span className="gold-text font-serif text-[10px] sm:text-sm tracking-widest">{t.pass}</span>
              <Plane size={13} className="text-[var(--gold)] rotate-45" />
            </div>
            <div className="mt-12 flex items-end gap-[2px] h-14 justify-center">
              {Array.from({ length: 34 }).map((_, i) => (
                <span key={i} className="bg-[var(--olive)]"
                  style={{ width: i % 3 === 0 ? 3 : 1.5, height: `${40 + ((i * 37) % 60)}%` }} />
              ))}
            </div>
            <div className="mt-6 space-y-4">
              <div>
                <p className="font-mono text-[8px] tracking-[0.2em] text-[var(--olive)]/60">{t.from}</p>
                <p className="font-serif font-bold text-2xl sm:text-3xl text-[var(--forest)] leading-none">{f.from}</p>
                <p className="font-sans text-[10px] text-[var(--olive)]/70">{f.fromCity}</p>
              </div>
              <div>
                <p className="font-mono text-[8px] tracking-[0.2em] text-[var(--olive)]/60">{t.to}</p>
                <p className="font-serif font-bold text-2xl sm:text-3xl text-[var(--forest)] leading-none">{f.to}</p>
                <p className="font-sans text-[10px] text-[var(--olive)]/70">{f.toCity}</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

const Cell = ({ label, value }) => (
  <div>
    <p className="font-mono text-[8px] sm:text-[9px] tracking-[0.18em] text-[var(--olive)]/55 uppercase">{label}</p>
    <p className="font-serif text-lg sm:text-2xl text-[var(--forest)] mt-0.5">{value}</p>
  </div>
);

const FlightPath = () => (
  <div className="relative w-16 sm:w-40 h-16 flex items-center justify-center">
    <svg viewBox="0 0 160 60" className="w-full h-full">
      <path d="M4 42 C 40 42, 45 8, 80 8 C 115 8, 120 42, 156 30"
        fill="none" stroke="var(--gold-dark)" strokeWidth="1.4" className="dash-path" />
      <path d="M60 6 C 72 -2, 88 -2, 100 6 C 90 16, 70 16, 60 6 Z"
        fill="none" stroke="var(--gold-dark)" strokeWidth="1.2" opacity="0.7" transform="translate(0,2)" />
      <circle cx="4" cy="42" r="2.5" fill="var(--gold-dark)" />
    </svg>
    <Plane className="absolute right-0 text-[var(--gold-dark)]" size={22} />
  </div>
);
